import { Search as SearchComponent, useSearch } from '@haus-storefront-react/search'
import { useEffect, useId, useRef, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchIcon } from './SearchIcon'
import { SearchPrefilledResults } from './SearchPrefilledResults'
import { SearchTypedResults } from './SearchTypedResults'
import type { SearchWidgetProps } from './types'
import styles from './Search.module.scss'

const REQUEST_FOCUS_EVENT_NAME = 'haus:request-focus'
const SEARCH_INPUT_SELECTOR = 'input[data-search-main-input="true"]'

interface SearchHostElement extends HTMLElement {
  focusSearchInput?: () => void
}

function focusMainSearchInput(hostElement: SearchHostElement): boolean {
  const input = hostElement.shadowRoot?.querySelector<HTMLInputElement>(SEARCH_INPUT_SELECTOR)
  if (!input) {
    return false
  }

  input.focus({ preventScroll: true })

  try {
    const selectionEnd = input.value.length
    input.setSelectionRange(selectionEnd, selectionEnd)
  } catch {
    // setSelectionRange can fail on non-text input types.
  }

  return true
}

const SearchWidget = ({ configStyles, collectionId }: SearchWidgetProps) => {
  const { t } = useTranslation()
  const searchInputId = useId()
  const searchRootRef = useRef<HTMLDivElement>(null)
  const pendingFocusObserverRef = useRef<MutationObserver | null>(null)
  const searchPlaceholder = t('searchField.searchPlaceholder')

  const effectiveCollectionId =
    collectionId != null && String(collectionId) !== '0' ? String(collectionId) : undefined

  useEffect(() => {
    const rootNode = searchRootRef.current?.getRootNode()
    if (!(rootNode instanceof ShadowRoot)) {
      return undefined
    }

    const clearPendingFocus = (): void => {
      if (pendingFocusObserverRef.current === null) {
        return
      }

      pendingFocusObserverRef.current.disconnect()
      pendingFocusObserverRef.current = null
    }

    const requestInputFocus = (hostElement: SearchHostElement): void => {
      clearPendingFocus()
      if (focusMainSearchInput(hostElement)) {
        return
      }

      const { shadowRoot } = hostElement
      if (!shadowRoot) {
        return
      }

      const observer = new MutationObserver(() => {
        if (focusMainSearchInput(hostElement)) {
          observer.disconnect()
          pendingFocusObserverRef.current = null
          return
        }
      })

      observer.observe(shadowRoot, { childList: true, subtree: true })
      pendingFocusObserverRef.current = observer
    }

    if (!(rootNode.host instanceof HTMLElement)) {
      return undefined
    }

    const hostElement: SearchHostElement = rootNode.host
    const requestFocusListener: EventListener = () => {
      requestInputFocus(hostElement)
    }

    hostElement.addEventListener(REQUEST_FOCUS_EVENT_NAME, requestFocusListener)

    const focusSearchInputMethod = (): void => {
      requestInputFocus(hostElement)
    }
    hostElement.focusSearchInput = focusSearchInputMethod

    return () => {
      clearPendingFocus()
      hostElement.removeEventListener(REQUEST_FOCUS_EVENT_NAME, requestFocusListener)

      if (hostElement.focusSearchInput === focusSearchInputMethod) {
        delete hostElement.focusSearchInput
      }
    }
  }, [])

  const prefilledSearch = useSearch({
    collectionId: effectiveCollectionId,
    term: '',
    take: 5,
    groupByProduct: true,
  })
  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== 'Enter') {
      return
    }

    const term = e.currentTarget.value.trim()
    if (!term) {
      return
    }

    e.preventDefault()
    window.location.assign(
      t('urls.search', {
        term: encodeURIComponent(term),
      }),
    )
  }

  return (
    <SearchComponent.Root term="" take={5} groupByProduct>
      {(searchContext) => {
        const searchTerm = searchContext.variables.term?.trim() ?? ''
        const isPrefilledMode = !searchTerm && !!effectiveCollectionId

        return (
          <div className={styles.searchRoot} ref={searchRootRef}>
            <label htmlFor={searchInputId} className={styles.searchLabel}>
              {searchPlaceholder}
            </label>
            <div className={styles.inputWrapper}>
              <SearchComponent.Input
                id={searchInputId}
                disabled={false}
                placeholder={searchPlaceholder}
                className={styles.searchInput}
                aria-label={searchPlaceholder}
                data-search-main-input="true"
                onKeyDown={handleInputKeyDown}
              />
              <span className={styles.searchIconWrapper} aria-hidden>
                <SearchIcon />
              </span>
            </div>

            {isPrefilledMode ? (
              <SearchPrefilledResults
                configStyles={configStyles}
                error={prefilledSearch.error}
                isLoading={prefilledSearch.isLoading}
                items={prefilledSearch.data?.items ?? []}
              />
            ) : (
              <SearchTypedResults configStyles={configStyles} searchTerm={searchTerm} />
            )}
          </div>
        )
      }}
    </SearchComponent.Root>
  )
}

export default SearchWidget
